import { useMemo, useState } from 'react'
import { redirect, useLocation, useNavigate } from 'react-router'
import { Button, Field, Input } from '~/components/ui'
import { getOperatorContext } from '../../server/lib/loader-context'
import { getOnboardingContinuation } from '../../server/lib/onboarding-continuation'
import { getPublicAuthenticationError } from '../../server/lib/public-auth-boundary'
import { signIn } from '../lib/auth.client'
import { Container, VStack, styled } from '../styled-system/jsx'
import type { Route } from './+types/login'

export function meta() {
    return [{ title: 'Login - Self-Service Portal' }, { name: 'description', content: 'Sign in to manage your venue or artist profile' }]
}

export async function loader({ request }: Route.LoaderArgs) {
    const continuation = getOnboardingContinuation(request.url)
    const operator = await getOperatorContext(request)

    if (operator) {
        throw redirect(
            operator.user.emailVerified
                ? continuation
                : `/verify-email?intent=venue&email=${encodeURIComponent(operator.user.email)}`,
        )
    }

    return {}
}

export default function Login() {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const location = useLocation()
    const redirectTo = useMemo(() => {
        return getOnboardingContinuation(`http://self-service.local${location.search}`)
    }, [location.search])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)

        void (async () => {
            try {
                const result = await signIn.email({
                    email,
                    password,
                    callbackURL: redirectTo,
                })

                if (result.error) {
                    setError(getPublicAuthenticationError(result.error.status))
                } else {
                    // Successfully logged in, redirect to intended page
                    void navigate(redirectTo)
                }
            } catch {
                setError(getPublicAuthenticationError(500))
            } finally {
                setLoading(false)
            }
        })()
    }

    return (
        <Container maxW="md" py={{ base: '12', md: '16' }}>
            <VStack gap="8" alignItems="stretch">
                <VStack gap="2" alignItems="center">
                    <styled.h1 fontSize="2xl" fontWeight="bold">
                        Sign in
                    </styled.h1>
                    <styled.p color="fg.muted" textAlign="center">
                        Sign in to manage your venue or continue listing a new venue
                    </styled.p>
                </VStack>

                <styled.form onSubmit={(e) => void handleSubmit(e)}>
                    <VStack gap="6" alignItems="stretch">
                        <Field.Root required>
                            <Field.Label>Email</Field.Label>
                            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required />
                        </Field.Root>

                        <Field.Root required>
                            <Field.Label>Password</Field.Label>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                required
                            />
                        </Field.Root>

                        <styled.a href="/account-recovery" color="accent.default" fontSize="sm" textAlign="right">
                            Forgot your password?
                        </styled.a>

                        {error && (
                            <styled.div color="red.500" fontSize="sm" role="alert">
                                {error}
                            </styled.div>
                        )}

                        <Button type="submit" size="lg" loading={loading} width="full">
                            {loading ? 'Signing in...' : 'Sign in'}
                        </Button>

                        {redirectTo !== '/' && (
                            <styled.p fontSize="sm" color="fg.muted" textAlign="center">
                                New to ENTX?{' '}
                                <styled.a href="/signup?intent=venue" color="accent.default" fontWeight="semibold">
                                    Create an account
                                </styled.a>
                            </styled.p>
                        )}
                    </VStack>
                </styled.form>
            </VStack>
        </Container>
    )
}
